/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Injectable } from '@angular/core';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { AppConfigService } from '@app/common/app-config.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import * as _ from 'underscore';
import { TranslateService } from '@ngx-translate/core';
import { AiFindingService } from '@app/common/ai-finding.service';
import { UtilService } from '@app/util/util.service';
import { appConstants } from '@app/common/app-constants';
import { MessageService } from '@core/lib/services/message.service';
import { NotSupportedException } from '@core/lib/exceptions';
import { isUndefined } from '@core/lib/helpers/helpers';

@Injectable({
  providedIn: 'root'
})
export class SearchCriteriaService {
  private criteria = {} as any;
  private userPreference = {} as any;
  private escapedToHtmlMap = {
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&#x2F;': '/'
  };
  private htmlToEscapedMap = {
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;'
  };

  constructor(
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private userPreferenceService: UserPreferenceService,
    private messageService: MessageService,
    private aiFindingService: AiFindingService,
    private utilService: UtilService
  ) {}

  isFilterCriteriaLoaded() {
    return this.searchFilterCriteriaService.filterCriteriaLoaded;
  }

  setCriterion(key, value) {
    if (value) {
      this.criteria[key] = value;
    } else {
      delete this.criteria[key];
    }
  }

  getCriterion(key) {
    if (!isUndefined(this.criteria[key])) {
      console.log('getCriteriao', this.criteria[key]);
      return JSON.parse(JSON.stringify(this.criteria[key]));
    }
  }

  getAllCriteria() {
    return JSON.parse(JSON.stringify(this.criteria));
  }

  setUserPreference(key, value) {
    this.userPreference[key] = value;
  }

  getUserPreference(key) {
    return _.isUndefined(this.userPreference[key]) ? false : this.userPreference[key];
  }

  clearAllCriteria() {
    Object.getOwnPropertyNames(this.criteria).forEach(key => {
      delete this.criteria[key];
    });
  }

  setInstituteField(fieldList) {
    if (_.isEmpty(this.searchFilterCriteriaService.getInstitutes())) {
      // decide whether to remove Institute
      fieldList = _.without(fieldList, 'Institute');
    }
    return fieldList;
  }

  getFilterFields() {
    return [
      'patientName',
      'mrn',
      'accession',
      'dobFromDate',
      'dobToDate',
      'referringPhysician',
      'assignee',
      'procedureText'
    ];
  }

  setNhiField(fieldList) {
    if (!this.appConfigService.isNHIEnabled()) {
      // decide whether to remove NHI number
      fieldList = _.without(fieldList, 'NHI No.');
    }
    return fieldList;
  }

  getSearchFieldOrder() {
    let searchFieldOrder = [
      'Patient Name',
      'MRN',
      'NHI No.',
      'Accession Number',
      'Order Number',
      'Time of Exam',
      'Verified Time/Date',
      'Date of Birth',
      'Modality',
      'Body Part',
      'Exam Status',
      'Report Status',
      'Exam Specialty',
      'Exam Description',
      'Department',
      'Department C-Key',
      'Initial Author',
      'Responsible Author',
      'Institute',
      'Patient Location',
      'Ordering Location',
      'Ordering Location C-Key',
      'Site',
      'Modality Station Name',
      'Findings',
      'Priority',
      'Exceptions',
      'Zero Image Exams',
      'Order By',
      'StatOnTop'
    ];

    searchFieldOrder = this.setInstituteField(searchFieldOrder);
    searchFieldOrder = this.setNhiField(searchFieldOrder);
    return searchFieldOrder;
  }

  getCustomWorklistFieldOrder() {
    let customWorklistFieldOrder = [
      'Worklist Name',
      'Order Number',
      'Time of Exam',
      'Verified Time/Date',
      'Modality',
      'Body Part',
      'Exam Status',
      'Report Status',
      'Exam Specialty',
      'Exam Description',
      'Department',
      'Department C-Key',
      'Protocol Status',
      'Initial Author',
      'Responsible Author',
      'Institute',
      'Patient Location',
      'Ordering Location',
      'Ordering Location C-Key',
      'Site',
      'Modality Station Name',
      'Findings',
      'Priority',
      'Exceptions',
      'Zero Image Exams',
      'Order By',
      'StatOnTop'
    ];

    if (!this.appConfigService.getProtocolEnabled()) {
      // decide whether to remove Protocol Status
      customWorklistFieldOrder = _.without(customWorklistFieldOrder, 'Protocol Status');
    }
    customWorklistFieldOrder = this.setInstituteField(customWorklistFieldOrder);

    return customWorklistFieldOrder;
  }

  getCriteriaFieldDefinitions(searchType?) {
    const advancedSearchCriteria = {
      'Patient Name': {
        label: 'Patient Name',
        displayText: this.translate.instant('advanceSearch.patientNameLabel'),
        placeholder: this.translate.instant('advanceSearch.patientNamePlaceHolderText'),
        key: 'patientName',
        type: 'text',
        searchCharLimit: 41,
        minChars: 2,
        showRecentlyUsed: true,
        replace: true,
        hasInfoIcon: true
      },
      MRN: {
        label: 'MRN',
        displayText: this.translate.instant('advanceSearch.mrnLabel'),
        placeholder: this.translate.instant('advanceSearch.mrnPlaceHolderText'),
        key: 'mrn',
        type: 'text',
        searchCharLimit: 64,
        minChars: 2,
        replace: true,
        showRecentlyUsed: true,
        minPrefixChars: 3,
        suffixChars: 4,
        specialCharacters: new RegExp(this.translate.instant('advanceSearch.specialCharactersRegexMRN')),
        allowPrefixSearch: true,
        allowSuffixSearch: true,
        hasInfoIcon: true
      },
      'NHI No.': {
        label: 'NHI No.',
        displayText: this.utilService.getLocalizedNHISpecificText(
          'advanceSearch.nhiLabel',
          this.appConfigService.getNHIType()
        ),
        placeholder: this.utilService.getLocalizedNHISpecificText(
          'advanceSearch.nhiPlaceHolderText',
          this.appConfigService.getNHIType()
        ),
        key: 'nhi',
        type: 'text',
        searchCharLimit: 64,
        minChars: 10,
        maxChars: 10,
        replace: true,
        showRecentlyUsed: true,
        specialCharacters: new RegExp(this.translate.instant('advanceSearch.specialCharactersNumbersOnly')),
        allowPrefixSearch: false,
        allowSuffixSearch: false,
        isEnabled: false,
        hasInfoIcon: true
      },
      'Accession Number': {
        label: 'Accession Number',
        displayText: this.translate.instant('advanceSearch.accessionNumberLabel'),
        placeholder: this.translate.instant('advanceSearch.accessionNumberPlaceHolderText'),
        key: 'accession',
        type: 'text',
        searchCharLimit: 32,
        minChars: 2,
        replace: true,
        showRecentlyUsed: true,
        minPrefixChars: 3,
        specialCharacters: new RegExp(this.translate.instant('advanceSearch.specialCharactersRegexAccessionNumber')),
        allowPrefixSearch: true,
        allowSuffixSearch: false,
        hasInfoIcon: true
      },
      'Order Number': {
        label: 'Order Number',
        displayText: this.translate.instant('advanceSearch.orderNumberLabel'),
        placeholder: this.translate.instant('advanceSearch.orderNumberPlaceHolderText'),
        key: 'orderNumber',
        type: 'text',
        searchCharLimit: 64,
        minChars: 2,
        replace: true,
        showRecentlyUsed: true
      },
      'Time of Exam': {
        label: 'Time of Exam',
        displayText: this.translate.instant('advanceSearch.timeOfExamLabel'),
        placeholder: this.translate.instant('advanceSearch.timeOfExamPlaceHolderText'),
        key: 'timeOfExam',
        type: 'dateRangePicker',
        replace: true,
        treatAllDatesAsFilter: true
      },
      'Verified Time/Date': {
        label: 'Verified Time/Date',
        displayText: this.translate.instant('label.verifiedTimeDate'),
        placeholder: 'Choose a Time Of Exam',
        key: 'verificationTimeOfExam',
        type: 'dateRangePicker',
        replace: true,
        treatAllDatesAsFilter: true
      },
      'Date of Birth': {
        label: 'Date of Birth',
        displayText: this.translate.instant('advanceSearch.dobLabel'),
        placeholder: this.translate.instant('advanceSearch.dobPlaceHolderText'),
        key: 'patientBirthDate',
        type: 'dateRangePicker',
        replace: true
      },
      Modality: {
        label: 'Modality',
        displayText: this.translate.instant('advanceSearch.modalityLabel'),
        placeholder: this.translate.instant('advanceSearch.modalityPlaceHolderText'),
        key: 'modality',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getModalities()
      },
      'Body Part': {
        label: 'Body Part',
        displayText: this.translate.instant('advanceSearch.bodyPartLabel'),
        placeholder: this.translate.instant('advanceSearch.bodyPartPlaceHolderText'),
        key: 'bodyParts',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getBodyParts()
      },
      'Exam Description': {
        label: 'Exam Description',
        displayText: this.translate.instant('advanceSearch.examDescriptionLabel'),
        placeholder: this.translate.instant('advanceSearch.examDescriptionPlaceHolderText'),
        key: 'descriptions',
        type: 'dropdown',
        replace: true,
        lists: [],
        isSearchable: true
      },
      'Exam Specialty': {
        label: 'Exam Specialty',
        displayText: this.translate.instant('advanceSearch.examSpecialtyLabel'),
        placeholder: this.translate.instant('advanceSearch.examSpecialtyPlaceHolderText'),
        key: 'specialties',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getExamSpecialties()
      },
      Department: {
        label: 'Department',
        displayText: this.translate.instant('advanceSearch.departmentLabel'),
        placeholder: this.translate.instant('advanceSearch.departmentPlaceHolderText'),
        key: 'departments',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getDepartments()
      },
      'Exam Status': {
        label: 'Exam Status',
        displayText: this.translate.instant('advanceSearch.examStatusLabel'),
        placeholder: this.translate.instant('advanceSearch.examStatusPlaceHolderText'),
        key: 'examStatus',
        type: 'dropdown',
        replace: true,
        lists: _.each(this.searchFilterCriteriaService.getExamStatuses(), status => {
          status.name = status.examStatusDescription;
        })
      },
      'Protocol Status': {
        label: 'Protocol Status',
        displayText: this.translate.instant('advanceSearch.protocolStatusLabel'),
        placeholder: this.translate.instant('advanceSearch.protocolStatusPlaceHolderText'),
        key: 'protocolStatus',
        type: 'dropdown',
        replace: true,
        lists: _.each(this.searchFilterCriteriaService.getProtocolStatuses(), status => {
          status.name = status.protocolStatusDescription;
        })
      },
      'Initial Author': {
        label: 'Initial Author',
        displayText: this.translate.instant('advanceSearch.initialAuthorLabel'),
        placeholder: this.translate.instant('advanceSearch.initialAuthorPlaceHolderText'),
        key: 'initialAuthor',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getRadiologists()
      },
      'Responsible Author': {
        label: 'Responsible Author',
        displayText: this.translate.instant('advanceSearch.responsibleAuthorLabel'),
        placeholder: this.translate.instant('advanceSearch.responsibleAuthorPlaceHolderText'),
        key: 'responsibleAuthor',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getRadiologists()
      },
      'Designated Author': {
        label: 'Designated Author',
        displayText: this.translate.instant('workloadManager.listContainer.label.designatedAuthor'),
        placeholder: this.translate.instant('advanceSearch.designatedAuthorPlaceHolderText'),
        key: 'designatedAuthor',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getRadiologists()
      },
      Institute: {
        label: 'Institute',
        displayText: this.translate.instant('advanceSearch.instituteLabel'),
        placeholder: this.translate.instant('advanceSearch.institutePlaceHolderText'),
        key: 'institute',
        type: 'dropdown',
        replace: true,
        lists: _.each(this.searchFilterCriteriaService.getInstitutes(), item => {
          item.name = item.instituteName;
        })
      },
      'Patient Location': {
        label: 'Patient Location',
        displayText: this.translate.instant('advanceSearch.patientLocationLabel'),
        placeholder: this.translate.instant('advanceSearch.patientLocationPlaceHolderText'),
        key: 'patientLocation',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getPatientLocations()
      },
      'Ordering Location': {
        label: 'Ordering Location',
        displayText: this.translate.instant('advanceSearch.orderingLocationLabel'),
        placeholder: this.translate.instant('advanceSearch.orderingLocationPlaceHolderText'),
        key: 'orderingLocation',
        type: 'dropdown',
        orderBy: 'name.length',
        replace: true,
        lists: this.searchFilterCriteriaService.getOrderingLocations()
      },
      'Worklist Name': {
        label: 'Worklist Name',
        displayText: this.translate.instant('advanceSearch.worklistNameLabel'),
        placeholder: this.translate.instant('advanceSearch.worklistNamePlaceHolderText'),
        key: 'customWorklistName',
        type: 'text',
        searchCharLimit: 50,
        replace: true,
        specialCharacters: new RegExp(this.translate.instant('advanceSearch.specialCharactersRegexWorklistName'))
      },
      'Report Status': {
        label: 'Report Status',
        displayText: this.translate.instant('advanceSearch.reportStatusLabel'),
        placeholder: this.translate.instant('advanceSearch.reportStatusPlaceHolderText'),
        key: 'ReportStatus',
        type: 'dropdown',
        replace: true,
        lists: _.each(this.searchFilterCriteriaService.getReportStatuses(), status => {
          status.name = status.reportStatusDescription;
        })
      },
      'Modality Station Name': {
        label: this.translate.instant('workloadManager.listContainer.label.modalityStationName'),
        displayText: this.translate.instant('advanceSearch.ModalityStationNameLabel'),
        placeholder: this.translate.instant('advanceSearch.ModalityStationNameLabelPlaceHolderText'),
        key: 'equipment',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getEquipment()
      },
      Site: {
        label: this.translate.instant('workloadManager.listContainer.label.site'),
        displayText: this.translate.instant('advanceSearch.SiteLabel'),
        placeholder: this.translate.instant('advanceSearch.SitePlaceHolderText'),
        key: 'site',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getSites()
      },
      'Department C-Key': {
        label: this.translate.instant('workloadManager.listContainer.label.deptCkey'),
        displayText: this.translate.instant('advanceSearch.DepartmentCKeyLabel'),
        placeholder: this.translate.instant('advanceSearch.DepartmentCKeyPlaceHolderText'),
        key: 'departments',
        type: 'text',
        searchCharLimit: 32,
        replace: true,
        specialCharacters: new RegExp(this.translate.instant('advanceSearch.specialCharactersNumbersOnly')),
        showRecentlyUsed: true
      },
      'Ordering Location C-Key': {
        label: this.translate.instant('workloadManager.listContainer.label.orderingLocationId'),
        displayText: this.translate.instant('advanceSearch.OrderingLocationCKeyLabel'),
        placeholder: this.translate.instant('advanceSearch.OrderingLocationCKeyPlaceHolderText'),
        key: 'orderingLocationId',
        type: 'text',
        searchCharLimit: 32,
        replace: true,
        specialCharacters: new RegExp(this.translate.instant('advanceSearch.specialCharactersNumbersOnly')),
        showRecentlyUsed: true
      },
      Findings: {
        label: 'Findings',
        displayText: this.translate.instant('advanceSearch.findingsLabel'),
        placeholder: this.translate.instant('advanceSearch.findingsPlaceHolderText'),
        key: 'aiFinding',
        type: 'dropdown',
        replace: true,
        displayLogo: true,
        logoClass: 'advance-robot',
        lists: this.searchFilterCriteriaService.getAiFindings()
      },
      Priority: {
        label: this.translate.instant('workloadManager.listContainer.label.priority'),
        displayText: this.translate.instant('workloadManager.listContainer.label.priority'),
        placeholder: this.translate.instant('advanceSearch.priorityPlaceHolderText'),
        key: 'priority',
        type: 'priorityDropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getExamPriorities()
      },
      Exceptions: {
        label: this.translate.instant('workloadManager.listContainer.label.exceptions'),
        displayText: this.translate.instant('workloadManager.listContainer.label.exceptions'),
        placeholder: this.translate.instant('advanceSearch.exceptionPlaceHolderText'),
        key: 'examFlags',
        type: 'dropdown',
        replace: true,
        lists: this.searchFilterCriteriaService.getExceptions()
      },
      'Zero Image Exams': {
        label: 'Zero Image Exams',
        key: 'excludeZeroImageExams',
        type: 'radio',
        replace: true
      },
      'Order By': {
        label: 'Order By',
        key: 'orderBy',
        type: 'radioButton',
        replace: true
      },
      StatOnTop: {
        label: 'Stat on Top',
        key: 'statOnTop',
        type: 'checkbox',
        replace: true
      }
    };
    const inlineFilterCriteriaWithLastFiveEntry = {
      'Exam Id': {
        key: 'requestedProcedureId',
        showRecentlyUsed: true
      }
    };
    if (searchType === 'InlineFilter') {
      advancedSearchCriteria['Department C-Key'].key = 'deptCkey';
      _.each(advancedSearchCriteria, (value: any, key: any) => {
        if (value.showRecentlyUsed === true) {
          inlineFilterCriteriaWithLastFiveEntry[key] = value;
        }
      });
      console.log('advance search criteria ', inlineFilterCriteriaWithLastFiveEntry);
      return inlineFilterCriteriaWithLastFiveEntry;
    }
    if (!this.aiFindingService.isShowAiFindingsOnlyAdminEnabled()) {
      delete advancedSearchCriteria.Findings;
    }
    if (this.appConfigService.getAppConfigByKey('cpacsVersion') === 'v7') {
      delete advancedSearchCriteria.Exceptions;
    }

    return advancedSearchCriteria;
  }

  getPreferenceDefinitions() {
    return [
      {
        displayText: this.translate.instant('advanceSearch.preferenceDisplayStatLabel'),
        label: this.translate.instant('advanceSearch.preferenceDisplayStatLabel'),
        preferenceType: 'advanceSearch',
        key: 'statsOnTop',
        type: 'checkbox'
      },
      {
        displayText: this.translate.instant('advanceSearch.preferenceViewerLaunchLabel'),
        label: this.translate.instant('advanceSearch.preferenceViewerLaunchLabel'),
        preferenceType: 'customWorklist',
        key: 'viewerPreferences',
        type: 'checkbox'
      }
    ];
  }

  embedDateRangeInCriteria(date, criteria) {
    if (date && date.selectedCode && date.selectedCode === 'Custom Range') {
      delete criteria.dateToken;
      criteria.beginDate = UtilService.formatToShortDate(new Date(date.oldest));
      criteria.endDate = UtilService.formatToShortDate(new Date(date.newest));
    } else if (date && date.selectedCode && date.selectedCode !== 'All Dates') {
      criteria.dateToken = date.selectedCode;
      delete criteria.beginDate;
      delete criteria.endDate;
    }
  }

  embedVerifiedDateRangeInCriteria(date, criteria) {
    if (date && date.selectedCode && date.selectedCode === 'Custom Range') {
      delete criteria.verifiedDateRangeToken;
      criteria.verifiedBeginDate = UtilService.formatToShortDate(new Date(date.oldest));
      criteria.verifiedEndDate = UtilService.formatToShortDate(new Date(date.newest));
    } else if (date && date.selectedCode && date.selectedCode !== 'All Dates') {
      criteria.verifiedDateRangeToken = date.selectedCode;
      delete criteria.verifiedBeginDate;
      delete criteria.verifiedEndDate;
    }
  }

  embedPatientBirthDateRangeInCriteria(date, criteria) {
    if (date && date.selectedCode && date.selectedCode === 'Custom Range') {
      delete criteria.patientBirthDateToken;
      criteria.patientBirthBeginDate = UtilService.formatToShortDate(new Date(date.oldest));
      criteria.patientBirthEndDate = UtilService.formatToShortDate(new Date(date.newest));
    } else if (date && date.selectedCode && date.selectedCode !== 'All Dates') {
      criteria.patientBirthDateToken = date.selectedCode;
      delete criteria.patientBirthBeginDate;
      delete criteria.patientBirthEndDate;
    }
  }

  /* Saving as custom worklists */
  criteriaContainQuickSearchFields(searchCriteria): boolean {
    const fields = this.getSearchFieldOrder();
    for (let i = 0; i !== 4; ++i) {
      if (searchCriteria[fields[i]] || searchCriteria === fields[i] || searchCriteria === 'MRN / Patient ID') {
        return true;
      }
    }
    return false;
  }

  criteriaContainFilterFields(searchCriteria): boolean {
    if (searchCriteria.filterParams && searchCriteria.filterParams.filter) {
      const fields = this.getFilterFields();
      for (let i = 0; i <= fields.length - 1; ++i) {
        if (searchCriteria.filterParams.filter[fields[i]]) {
          return true;
        }
      }
    }
    return false;
  }

  criteriaContainModalities(searchCriteria) {
    const modalities = searchCriteria && searchCriteria.Modality;
    return modalities && 0 < modalities.length;
  }

  criteriaContainInstitutes(searchCriteria) {
    const institutes = searchCriteria && searchCriteria.Institute;
    return institutes && 0 < institutes.length;
  }

  criteriaContainReportStatuses(searchCriteria) {
    const reportStatuses = searchCriteria && searchCriteria['Report Status'];
    return reportStatuses && 0 < reportStatuses.length;
  }

  criteriaContainInitialAuthor(searchCriteria) {
    const initialAuthors = searchCriteria && searchCriteria['Initial Author'];
    return initialAuthors && 0 < initialAuthors.length;
  }

  criteriaContainResponsibleAuthor(searchCriteria) {
    const responsibleAuthors = searchCriteria && searchCriteria['Responsible Author'];
    return responsibleAuthors && 0 < responsibleAuthors.length;
  }

  criteriaContainDesignatedAuthor(searchCriteria) {
    const designatedAuthors = searchCriteria && searchCriteria['Designated Author'];
    return designatedAuthors && 0 < designatedAuthors.length;
  }

  criteriaContainOrderingLocation(searchCriteria) {
    const orderingLocations = searchCriteria && searchCriteria['Ordering Location'];
    return orderingLocations && 0 < orderingLocations.length;
  }

  criteriaContainExamStatus(searchCriteria?) {
    const examStatuses = searchCriteria && searchCriteria['Exam Status'];
    return examStatuses && 0 < examStatuses.length;
  }

  criteriaContainsDateOfBirth(searchCriteria) {
    const dateOfBirth = searchCriteria && searchCriteria['Date of Birth'];
    return dateOfBirth && 0 < dateOfBirth.selectedCode.length && dateOfBirth.selectedCode !== 'All Dates';
  }

  criteriaContainsOrderNumber(searchCriteria) {
    const orderNumber = searchCriteria && searchCriteria['Order Number'];
    return orderNumber && 0 < orderNumber.value.length;
  }

  getCustomWlCriteriaList(functionName) {
    if (!this[functionName]()) {
      return null;
    }
  }

  convertSearchCriteriaToCustomWorklist(searchCriteria, filterParams): any {
    const mapping = appConstants.CUSTOM_WORKLIST_KEYS;
    const criteriaObject = {};

    this.embedDateRangeInCriteria(searchCriteria['Time of Exam'], searchCriteria);
    this.embedVerifiedDateRangeInCriteria(searchCriteria['Verified Time/Date'], searchCriteria);

    if (searchCriteria.beginDate && searchCriteria.endDate) {
      // custom worklist api apparently needs time of day.
      searchCriteria.beginDate += ' 12:00:00 AM';
      searchCriteria.endDate += ' 23:59:59 PM';
    }

    if (searchCriteria.verifiedBeginDate && searchCriteria.verifiedEndDate) {
      // custom worklist api apparently needs time of day.
      searchCriteria.verifiedBeginDate += ' 12:00:00 AM';
      searchCriteria.verifiedEndDate += ' 23:59:59 PM';
    }

    if (!this.criteriaContainExamStatus()) {
      criteriaObject[`examStatusList`] = _.pluck(
        this.searchFilterCriteriaService.getExamStatuses(),
        'examStatusNumber'
      );
    }

    criteriaObject[`modalityList`] = this.getCustomWlCriteriaList('criteriaContainModalities');
    criteriaObject[`instituteList`] = this.getCustomWlCriteriaList('criteriaContainInstitutes');
    criteriaObject[`reportStatusList`] = this.getCustomWlCriteriaList('criteriaContainReportStatuses');
    criteriaObject[`initialInterpreterList`] = this.getCustomWlCriteriaList('criteriaContainInitialAuthor');
    criteriaObject[`radiologistList`] = this.getCustomWlCriteriaList('criteriaContainResponsibleAuthor');
    criteriaObject[`designatedAuthorList`] = this.getCustomWlCriteriaList('criteriaContainDesignatedAuthor');
    criteriaObject[`orderingLocationList`] = this.getCustomWlCriteriaList('criteriaContainOrderingLocation');
    criteriaObject[`worklistParent`] = searchCriteria.parentWorklistId;
    criteriaObject[`isLinked`] = _.isEmpty(searchCriteria.isLinked) ? false : searchCriteria.isLinked;
    if (!!filterParams) {
      const filterJson = [];
      _.each(filterParams.filter, (param, key) => {
        filterJson.push(key + ':' + param.toString());
      });
      criteriaObject[`filterParamsList`] = filterJson;
    }

    Object.getOwnPropertyNames(searchCriteria).forEach(criteriaValue => {
      const value = mapping[criteriaValue];

      const criterion = searchCriteria[criteriaValue];
      if (value && typeof value === 'object') {
        if (Array.isArray(criterion)) {
          criteriaObject[value.key] = _.pluck(criterion, value.field);
        } else if (criterion[value.field]) {
          criteriaObject[value.key] = criterion[value.field];
        }
      } else if (value && typeof value === 'string') {
        criteriaObject[value] = criterion;
      }
      if (criterion.key === appConstants.EXCLUDE_ZERO_IMAGE.key) {
        criteriaObject[appConstants.EXCLUDE_ZERO_IMAGE.key] = criterion.state;
      }
      if (criterion.key === appConstants.ORDER_BY.key) {
        criteriaObject[appConstants.ORDER_BY.key] = criterion.state;
      }
    });

    return criteriaObject;
  }

  convertCustomWorklistToSearchCriteria(customWorklist) {
    const translatedSearchCriteria = {};

    translatedSearchCriteria['Time of Exam'] = this.extractDate(customWorklist);
    translatedSearchCriteria['Verified Time/Date'] = this.extractVerifiedDate(customWorklist);

    _.keys(customWorklist).forEach(customWorklistFilterName => {
      console.log('filter name', customWorklistFilterName);
      const mapping = appConstants.CUSTOM_WORKLIST_TO_ADVANCED_SEARCH_MAP[customWorklistFilterName];
      if (!mapping) {
        throw new NotSupportedException(
          'Unknown custom worklist field ' + customWorklistFilterName + ' received. Unable to convert.'
        );
      }

      const cwFieldValue = customWorklist[customWorklistFilterName];
      if (!mapping.ignore && null !== cwFieldValue && UtilService.isDefined(cwFieldValue)) {
        translatedSearchCriteria[mapping.mapsTo] = this.convertCustomWorklistFieldToSearchField(cwFieldValue, mapping);
      }
    });

    console.log('convertCustomWorklistToSearchCriteria', translatedSearchCriteria);
    return translatedSearchCriteria;
  }

  extractDate(customWorklist) {
    const newDate = {} as any;
    if (customWorklist.examDateRangeToken) {
      newDate.selectedCode = customWorklist.examDateRangeToken;
    } else if (customWorklist.beginExamDateRange) {
      newDate.selectedCode = 'Custom Range';
      newDate.oldest = new Date(customWorklist.beginExamDateRange);
      newDate.newest = new Date(customWorklist.endExamDateRange);
    } else {
      newDate.selectedCode = 'All Dates';
    }
    return newDate;
  }

  extractVerifiedDate(customWorklist) {
    const newDate = {} as any;
    if (customWorklist.verifiedDateRangeToken) {
      newDate.selectedCode = customWorklist.verifiedDateRangeToken;
    } else if (customWorklist.verifiedBeginDate) {
      newDate.selectedCode = 'Custom Range';
      newDate.oldest = new Date(customWorklist.verifiedBeginDate);
      newDate.newest = new Date(customWorklist.verifiedEndDate);
    } else {
      newDate.selectedCode = 'All Dates';
    }
    return newDate;
  }

  convertCustomWorklistFieldToSearchField(customWorklistField, mapping) {
    let result;

    if (mapping.type === 'list') {
      result = [];
      customWorklistField.forEach(value => {
        const item = mapping.generateEntry ? this.createGenericEntry(value) : this.lookupSearchListItem(value, mapping);
        // if there was no match, discard this value
        if (item) {
          result.push(item);
        }
      });
    } else if (mapping.type === 'text') {
      result = { type: 'text', value: UtilService.convertValueToType(customWorklistField, mapping.convertValuesTo) };
    } else if (mapping.type === 'radio') {
      if (mapping.mapsTo === appConstants.ORDER_BY.mapsTo) {
        result = { type: 'radio', state: customWorklistField, key: appConstants.ORDER_BY.key };
      } else {
        result = { type: 'radio', state: customWorklistField, key: appConstants.EXCLUDE_ZERO_IMAGE.key };
      }
    } else {
      throw new NotSupportedException('Unsupported mapping type ' + mapping.type + ' specified.');
    }

    return result;
  }

  lookupSearchListItem(value, mapping) {
    value = UtilService.convertValueToType(value, mapping.convertValuesTo);
    const list = this.getCriteriaFieldDefinitions()[mapping.mapsTo].lists;
    for (let i = 0, length = list.length; i !== length; ++i) {
      if (value == list[i][mapping.listItemKeyName]) {
        return list[i];
      }
    }
    return undefined;
  }

  createGenericEntry(value) {
    return {
      id: value,
      name: value
    };
  }

  persistSearchAndFilterEntries(allCriteria, criteriaHistoryType) {
    const userPreference = {};
    const searchCriteriaObj =
      this.userPreferenceService.getUserLevelPreferenceFromCache(
        appConstants.CRITERIA_HISTORY_GROUP_MAPPING[criteriaHistoryType]
      ) || {};
    let isCriteriaChanged = false;
    const criteriaFieldDef = this.getCriteriaFieldDefinitions(criteriaHistoryType);
    const criteriaKeys = Object.keys(allCriteria);
    _.each(criteriaKeys, criteriaKey => {
      if (criteriaFieldDef && criteriaFieldDef[criteriaKey] && criteriaFieldDef[criteriaKey].showRecentlyUsed) {
        const entries = [];
        entries.push(allCriteria[criteriaKey].value);
        const searchCriteriaKey = criteriaFieldDef[criteriaKey].key;
        const existingEntries = searchCriteriaObj[searchCriteriaKey];
        isCriteriaChanged = isCriteriaChanged || (existingEntries ? existingEntries[0] !== entries[0] : true);
        const combinedSearchEntries = existingEntries ? _.union(entries, existingEntries) : entries;
        searchCriteriaObj[searchCriteriaKey] = _.first(combinedSearchEntries, 5);
      }
    });
    if (isCriteriaChanged && criteriaHistoryType) {
      userPreference[appConstants.CRITERIA_HISTORY_GROUP_MAPPING[criteriaHistoryType]] = searchCriteriaObj;
      this.userPreferenceService.createOrUpdateUserLevelPreference(userPreference);
      const broadcastEventType = criteriaHistoryType + 'CriteriaHistoryChanged';
      this.messageService.broadcast(broadcastEventType, allCriteria);
    }
  }

  getPersistedCriteriaList(key, criteriaHistoryType) {
    const criteriaTypeMapping = appConstants.CRITERIA_HISTORY_GROUP_MAPPING[criteriaHistoryType];
    const persistedSearchCriteria = this.userPreferenceService.getUserLevelPreferenceFromCache(criteriaTypeMapping);
    return persistedSearchCriteria ? persistedSearchCriteria[key] : [];
  }

  filterByEncoded(typeAheadOption, typedString) {
    const escapedTypedString = this.escapeHtml(typedString);
    return escapedTypedString === typeAheadOption.substr(0, escapedTypedString.length);
  }

  escapeHtml(str) {
    return String(str).replace(/[<>"'/]/g, s => {
      return this.htmlToEscapedMap[s];
    });
  }

  encodeHtml(str) {
    const keys = Object.keys(this.escapedToHtmlMap);
    for (const key in keys) {
      if ({}.hasOwnProperty.call(keys, key)) {
        str = str.split(keys[key]).join(this.escapedToHtmlMap[keys[key]]); // replaces escaped html with raw html
      }
    }
    return str;
  }

  setupHtmlEscapedQuickSearchObject(searchCriteriaObject) {
    const htmlEscapedSearchCriteriaObject = {};
    _.each(searchCriteriaObject, (array: any, key: any) => {
      const filteredArr = [];
      _.each(array, element => {
        filteredArr.push(this.escapeHtml(element));
      });
      htmlEscapedSearchCriteriaObject[key] = filteredArr;
    });
    return htmlEscapedSearchCriteriaObject;
  }

  setupHtmlEscapedAdvancedSearchObject(searchCriteriaObject) {
    const htmlEscapedSearchCriteriaObject = [];
    _.each(searchCriteriaObject, element => {
      htmlEscapedSearchCriteriaObject.push(this.escapeHtml(element));
    });
    return htmlEscapedSearchCriteriaObject;
  }
}
